import { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

// Set the PDF worker from CDN
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

interface PdfViewerProps {
  fileUrl: string;
}

const PdfViewer: React.FC<PdfViewerProps> = ({ fileUrl }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPDF = async () => {
      try {
        setLoading(true);
        const loadingTask = pdfjsLib.getDocument(fileUrl);
        const pdf = await loadingTask.promise;

        const container = containerRef.current;
        if (container) container.innerHTML = '';

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);

          if (!container) continue;

          const containerWidth = container.offsetWidth;
          const unscaledViewport = page.getViewport({ scale: 1 });
          const scale = containerWidth / unscaledViewport.width;

          const viewport = page.getViewport({ scale });

          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');

          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.display = 'block';
          canvas.style.margin = '0 auto';

          if (context) {
            await page.render({ canvasContext: context, viewport }).promise;
            container.appendChild(canvas);
          }
        }

        setLoading(false);
      } catch (error) {
        console.error('Failed to load PDF:', error);
        setLoading(false);
      }
    };

    if (fileUrl) loadPDF();

    const handleResize = () => {
      if (fileUrl) loadPDF();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [fileUrl]);

  return (
    <div className="relative max-h-[90vh] overflow-y-auto overflow-x-hidden px-4">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
          <div className="text-gray-600 font-semibold animate-pulse">
            Loading PDF...
          </div>
        </div>
      )}
      <div ref={containerRef} className="space-y-4" />
    </div>
  );
};

export default PdfViewer;
